import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import {
  BulkAssignment,
  BulkAssignmentDto,
  ErrorResponse,
} from 'apis/backendApi';
import { getApiAccessToken, getApiLanguage, getApiRoot } from 'core/apiConfig';
import { selectApiQueryContext } from 'core/redux/apiQueryContextSlice';
import { RootState } from 'core/store';
import { startSpanTelemetry } from 'core/util/telemetry';

export enum ImportStage {
  FILE = 'file',
  ASSIGNMENTS = 'assignments',
  COMPLETE = 'complete',
}

export type AssignmentStatus = 'processing' | 'completed' | 'error';

export type ImportState = {
  stage: ImportStage;
  bulkAssignments?: BulkAssignmentDto;
  errorResponse?: ErrorResponse;
  assignmentStatuses: Record<number, AssignmentStatus>;
};

const TARGET_CHUNK_SIZE = 10;

const importSlice = createSlice({
  name: 'import',
  initialState: {
    stage: ImportStage.FILE,
    assignmentStatuses: {},
  } as ImportState,
  reducers: {
    reset(state) {
      state.stage = ImportStage.FILE;
      delete state.bulkAssignments;
      delete state.errorResponse;
      state.assignmentStatuses = {};
    },

    completeImport(state, action: PayloadAction<BulkAssignmentDto>) {
      state.bulkAssignments = action.payload;
      state.stage = ImportStage.ASSIGNMENTS;
    },

    completeBulkAssignment(state) {
      delete state.errorResponse;
      state.stage = ImportStage.COMPLETE;
    },

    setErrorResponse(state, action: PayloadAction<ErrorResponse>) {
      state.errorResponse = action.payload;
    },

    retry(state) {
      delete state.errorResponse;
      state.assignmentStatuses = {};
    },

    clearError(state) {
      delete state.errorResponse;
    },

    updateAssignmentStatuses(
      state,
      action: PayloadAction<{ rowNumbers: number[]; status: AssignmentStatus }>
    ) {
      for (const rowNumber of action.payload.rowNumbers) {
        state.assignmentStatuses[rowNumber] = action.payload.status;
      }
    },
  },
});

export const selectImportState = (root: RootState) => root.import;

export const { reset, retry, clearError } = importSlice.actions;

export default importSlice;

export const excelImport = createAsyncThunk(
  'import/excelImport',
  async (file: File, { dispatch, getState }) => {
    const { completeImport, setErrorResponse } = importSlice.actions;
    const { buCode } = selectApiQueryContext(getState() as RootState);
    const apiUrl = new URL(
      `${getApiRoot()}/slm/backend/items/v1/${buCode}/excel-import`
    );

    const formData = new FormData();
    formData.append('file', file);

    const logParsePerf = startSpanTelemetry('slm.import.parse', {
      bu_code: buCode,
    });
    let assignmentCount = 0;
    let outcome: 'success' | 'error' | 'exception' = 'exception';

    try {
      const response = await fetch(apiUrl.toString(), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getApiAccessToken()}`,
          'Accept-Language': getApiLanguage(),
        },
        body: formData,
      });

      const json = await response.json();
      if (response.ok) {
        assignmentCount = json.assignments?.length ?? 0;
        outcome = 'success';
        dispatch(completeImport(json));
      } else {
        outcome = 'error';
        dispatch(setErrorResponse(json));
      }
    } catch (error) {
      dispatch(setErrorResponse(error));
    } finally {
      logParsePerf({
        file_size_bytes: file.size,
        assignment_count: assignmentCount,
        outcome,
      });
    }
  }
);

export function chunkAssignmentsByArticle(
  assignments: BulkAssignment[],
  targetChunkSize: number
): BulkAssignment[][] {
  // Group assignments by itemNo
  const groupMap = new Map<string, BulkAssignment[]>();
  for (const a of assignments) {
    if (!a.itemNo) {
      throw new Error('in chunkAssignmentsByArticle itemNo is missing');
    }
    const group = groupMap.get(a.itemNo);
    if (group) {
      group.push(a);
    } else {
      groupMap.set(a.itemNo, [a]);
    }
  }

  if (groupMap.size === 0) return [];

  // Sort groups biggest first
  const groups = Array.from(groupMap.values()).sort(
    (a, b) => b.length - a.length
  );

  const chunks: BulkAssignment[][] = [];
  let startIndex = 0;
  let endIndex = groups.length - 1;

  while (startIndex <= endIndex) {
    // Always grab 1 group from the start (largest remaining), regardless of size
    const newChunk: BulkAssignment[] = [...groups[startIndex]];
    startIndex++;

    // Fill from the leading side while chunk is under target size
    while (startIndex <= endIndex && newChunk.length < targetChunkSize) {
      if (groups[startIndex].length + newChunk.length > targetChunkSize) {
        break;
      }
      newChunk.push(...groups[startIndex]);
      startIndex++;
    }

    // Fill from the trailing side (smallest groups) while chunk is under target size
    while (startIndex <= endIndex && newChunk.length < targetChunkSize) {
      if (groups[endIndex].length + newChunk.length > targetChunkSize) {
        break;
      }
      newChunk.push(...groups[endIndex]);
      endIndex--;
    }

    chunks.push(newChunk);
  }

  return chunks;
}

export const bulkAssign = createAsyncThunk(
  'import/bulkAssign',
  async (assignments: BulkAssignment[], { dispatch, getState }) => {
    const {
      completeBulkAssignment,
      setErrorResponse,
      updateAssignmentStatuses,
    } = importSlice.actions;
    const { buCode } = selectApiQueryContext(getState() as RootState);

    const apiUrl = new URL(
      `${getApiRoot()}/slm/backend/items/v1/${buCode}/bulk-assignment`
    );

    const chunks = chunkAssignmentsByArticle(assignments, TARGET_CHUNK_SIZE);

    const logBulkAssignmentPerf = startSpanTelemetry('slm.import.bulk_assign', {
      bu_code: buCode,
    });

    const results = await Promise.allSettled(
      chunks.map(async chunk => {
        const rowNumbers = chunk
          .map(a => a.rowNumber)
          .filter((r): r is number => r != null);

        dispatch(
          updateAssignmentStatuses({ rowNumbers, status: 'processing' })
        );

        try {
          const response = await fetch(apiUrl.toString(), {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${getApiAccessToken()}`,
              'Accept-Language': getApiLanguage(),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ assignments: chunk } as BulkAssignmentDto),
          });
          if (response.status !== 200) {
            throw await response.json();
          }
          dispatch(
            updateAssignmentStatuses({ rowNumbers, status: 'completed' })
          );
        } catch (error) {
          dispatch(updateAssignmentStatuses({ rowNumbers, status: 'error' }));
          try {
            Sentry.captureException(error, {
              extra: { bu_code: buCode },
            });
          } catch {}
          throw error;
        }
      })
    );

    const failed = results.filter(
      (r): r is PromiseRejectedResult => r.status === 'rejected'
    );

    logBulkAssignmentPerf({
      assignment_count: assignments.length,
      chunk_count: chunks.length,
      success_count: results.length - failed.length,
      error_count: failed.length,
      outcome: failed.length === 0 ? 'success' : 'partial_error',
    });

    if (failed.length === 0) {
      dispatch(completeBulkAssignment());
    } else {
      dispatch(setErrorResponse(failed[0].reason));
    }
  }
);
